import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, BellOff } from "lucide-react";
import { calFetch } from "@/lib/calApi";
import { currentSubscription, getOrCreateSubscription, pushSupported, subscriptionKeyMatches } from "@/lib/pushNotifications";

const FLAG = "cal_push_on";

export function CalPushCard({ compact }) {
  const [on, setOn] = useState(localStorage.getItem(FLAG) === "1");
  const [busy, setBusy] = useState(false);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    if (!on) return;
    currentSubscription().then(async (s) => {
      if (s && subscriptionKeyMatches(s)) return;
      // Cambiaron las claves de avisos (o se perdió la suscripción): re-suscribimos en silencio.
      try {
        if (Notification.permission !== "granted") throw new Error("sin permiso");
        const sub = await getOrCreateSubscription();
        await calFetch("/cal/subscribe-push", { method: "POST", body: { subscription: sub.toJSON(), tz, old_endpoint: s ? s.endpoint : undefined } });
      } catch {
        localStorage.removeItem(FLAG);
        setOn(false);
      }
    });
  }, [on, tz]);

  if (!pushSupported()) {
    if (compact) return null;
    return <p data-testid="cal-push-unsupported" className="text-sm text-cocoa-soft italic">Este navegador no soporta notificaciones. En iPhone, agrega la app a tu pantalla de inicio primero.</p>;
  }
  if (compact && on) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (on) {
        const s = await currentSubscription();
        if (s) await calFetch("/cal/unsubscribe-push", { method: "POST", body: { endpoint: s.endpoint } });
        localStorage.removeItem(FLAG);
        setOn(false);
        toast("Listo, no te avisamos más");
      } else {
        const sub = await getOrCreateSubscription();
        await calFetch("/cal/subscribe-push", { method: "POST", body: { subscription: sub.toJSON(), tz } });
        localStorage.setItem(FLAG, "1");
        setOn(true);
        toast.success("¡Bacán! Te avisamos todos los días a las 9:00");
      }
    } catch (e) {
      toast.error(e.message || "No pudimos activar las notificaciones");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="cal-push-card" className="rounded-3xl bg-night cal-grain text-white p-5 flex items-center gap-4">
      <div className="shrink-0 w-11 h-11 rounded-2xl bg-candle/15 flex items-center justify-center">
        {on ? <BellRing className="w-5 h-5 text-candle" /> : <BellOff className="w-5 h-5 text-candle" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-xl leading-tight">{on ? "Avisos activados" : "¿Te avisamos cada mañana?"}</p>
        <p className="text-xs text-white/65 mt-0.5">A las 9:00 de tu hora local ({tz}), cuando haya cumples o fiestas.</p>
      </div>
      <button data-testid="cal-push-toggle" onClick={toggle} disabled={busy}
        className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold cal-press disabled:opacity-50 ${on ? "bg-white/10 text-white" : "bg-candle text-night"}`}>
        {busy ? "…" : on ? "Apagar" : "Activar"}
      </button>
    </div>
  );
}
